// src/pages/container_rental/ContainerRentalListPage.tsx
//
// 세금계산서 발행 목록.
//
// 구 "Cargo Manifest Console"(다크 슬랩 헤더 · hazard tape · mono 대문자 코드
// 라벨 · radius 0 · 3px 오프셋 그림자 · 호버 translate)을 걷어내고 확정 디자인
// 시스템(HomePage / DashboardLayout 과 동일한 언어)으로 맞췄다.
//  · 페이지 헤더 = 제목 22px 700 + 서브 13.5px + 우측 액션(outlined / contained)
//  · KPI 4칸 = 흰 카드 + #E4E7EA 보더 + radius 12, 한글 라벨 · tabular 수치
//  · 카드 그리드 / DataGrid / 더 보기 / 뷰 토글은 기능 그대로, 시각만 교체

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import GridViewIcon from "@mui/icons-material/GridView";
import TableRowsIcon from "@mui/icons-material/TableRows";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { saveAs } from "file-saver";
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridDeleteIcon,
} from "@mui/x-data-grid-pro";

import {
  ContainerRentalDto,
  RentalListParams,
  RentalStatus,
} from "../../contracts/container_rental/types.ts";
import { designTokens } from "../../theme.ts";
import { useContainerRentalApi } from "../../hooks/useContainerRentalApi.ts";
import RentalSearchBar from "./components/RentalSearchBar.tsx";
import RentalCard from "./components/RentalCard.tsx";
import StatusChip from "./components/StatusChip.tsx";
import ConfirmDialog from "../estimates/ConfirmDialog.tsx";

const T = designTokens;

const C = {
  canvas: "#F6F7F8",
  surface: "#FFFFFF",
  field: "#F9FAFB",
  border: "#E4E7EA",
  rowLine: "#EDEFF1",
  ink: T.ink,
  sub: "#6B7178",
  caption: "#9AA0A6",
  accent: T.cargo,
  accentTint: "#FDEDE8",
  accentHover: "#C93A14",
  neutral: "#F2F4F5",
};

/** 카드 / 표 2단 뷰. ?view= 쿼리스트링과 양방향 동기화된다.
    타임라인은 독립 페이지({basePath}/timeline)로 분리되었다. */
type ViewMode = "cards" | "grid";
const VIEW_MODES: ViewMode[] = ["cards", "grid"];
const parseView = (v: string | null): ViewMode =>
  VIEW_MODES.includes(v as ViewMode) ? (v as ViewMode) : "cards";

const DEFAULT_PARAMS: RentalListParams = {
  q: "",
  status: "",
  page: 0,
  size: 20,
  sort: "updated_at_desc",
};

const fmtDate = (iso: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
};

const fmtKrw = (n: number) =>
  Number.isFinite(n) ? new Intl.NumberFormat("ko-KR").format(n) : "";

export default function ContainerRentalListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const inDashboard = location.pathname.startsWith("/dashboard");
  const basePath = inDashboard ? "/dashboard/container-rental" : "/container-rental";

  const {
    listRentals,
    deleteRental,
    downloadRentalExcel,
    downloadAllRentalsExcel,
  } = useContainerRentalApi();

  const [params, setParams] = useState<RentalListParams>(DEFAULT_PARAMS);
  const [items, setItems] = useState<ContainerRentalDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<ViewMode>(() =>
    parseView(searchParams.get("view"))
  );

  const [snack, setSnack] = useState<{ msg: string; severity: "error" | "success" } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ContainerRentalDto | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const fetchList = useCallback(async (p: RentalListParams) => {
    setLoading(true);
    try {
      const res = await listRentals(p);
      setItems(res.items ?? []);
      setTotal(res.total ?? 0);
    } catch (e: any) {
      setSnack({ msg: e?.message ?? "목록을 불러오지 못했습니다.", severity: "error" });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList(params);
  }, [params, fetchList]);

  // 하위 호환: 예전 ?view=gantt 링크는 독립 타임라인 페이지로 넘긴다.
  // 다른 경로로 나가면서 이 컴포넌트가 언마운트되므로 루프가 생기지 않는다.
  const legacyGantt = searchParams.get("view") === "gantt";
  useEffect(() => {
    if (legacyGantt) navigate(`${basePath}/timeline`, { replace: true });
  }, [legacyGantt, basePath, navigate]);

  // URL → state (뒤로가기 / 링크 진입)
  useEffect(() => {
    const next = parseView(searchParams.get("view"));
    setView((prev) => (prev === next ? prev : next));
  }, [searchParams]);

  // state → URL. 기본값(cards)은 파라미터를 남기지 않는다.
  // 리다이렉트 중에는 건드리지 않는다 — 여기서 setSearchParams 를 호출하면
  // 위 navigate 와 같은 커밋에서 경쟁해 타임라인 이동을 덮어쓴다.
  useEffect(() => {
    if (legacyGantt) return;
    const current = searchParams.get("view");
    const desired = view === "cards" ? null : view;
    if (current === desired) return;
    const next = new URLSearchParams(searchParams);
    if (desired) next.set("view", desired);
    else next.delete("view");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const handleParamsChange = (next: RentalListParams) => setParams(next);
  const handleOpen = (id: string) => navigate(`${basePath}/${id}`);
  const handleDelete = (rental: ContainerRentalDto) => setDeleteTarget(rental);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteSubmitting(true);
    try {
      await deleteRental(deleteTarget.id);
      setDeleteTarget(null);
      setSnack({ msg: "삭제되었습니다.", severity: "success" });
      await fetchList(params);
    } catch (e: any) {
      setSnack({ msg: e?.message ?? "삭제 실패", severity: "error" });
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleDownloadOne = async (rental: ContainerRentalDto) => {
    try {
      const { blob, filename } = await downloadRentalExcel(rental.id);
      saveAs(blob, filename);
    } catch (e: any) {
      setSnack({ msg: e?.message ?? "엑셀 다운로드 실패", severity: "error" });
    }
  };

  const handleDownloadAll = async () => {
    try {
      const { blob, filename } = await downloadAllRentalsExcel({
        q: params.q,
        status: params.status,
      });
      saveAs(blob, filename);
    } catch (e: any) {
      setSnack({ msg: e?.message ?? "전체 엑셀 다운로드 실패", severity: "error" });
    }
  };

  const handleLoadMore = () => {
    setParams((prev) => ({ ...prev, size: (prev.size ?? 20) + 20 }));
  };

  const stats = useMemo(() => {
    const counter: Record<RentalStatus, number> = {
      draft: 0, active: 0, paused: 0, closed: 0, cancelled: 0,
    };
    items.forEach((r) => { counter[r.status] = (counter[r.status] ?? 0) + 1; });
    return counter;
  }, [items]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: "status", headerName: "상태", width: 130,
        renderCell: ({ value }) => <StatusChip status={value} /> },
      { field: "clientName", headerName: "거래처", flex: 1, minWidth: 140 },
      { field: "siteName", headerName: "현장", flex: 1, minWidth: 140 },
      { field: "containerModel", headerName: "모델", width: 120 },
      { field: "containerCount", headerName: "수량", width: 70, type: "number",
        align: "right", headerAlign: "right",
        valueFormatter: (value: any) => `${value}대` },
      { field: "monthlyRent", headerName: "월 공급가", width: 130, type: "number",
        align: "right", headerAlign: "right",
        renderCell: ({ value }) => <>{fmtKrw(value)}</> },
      { field: "rentalStartDate", headerName: "1회차 시작", width: 120,
        renderCell: ({ value }) => <>{fmtDate(value)}</> },
      { field: "modifiedTimestamp", headerName: "수정일", width: 130,
        renderCell: ({ value }) => <>{fmtDate(value)}</> },
      { field: "actions", type: "actions", width: 100,
        getActions: (p) => [
          <GridActionsCellItem key="open" icon={<AddIcon />} label="열기"
            onClick={() => handleOpen(String(p.id))} showInMenu={false} />,
          <GridActionsCellItem key="excel" icon={<FileDownloadIcon />} label="엑셀"
            onClick={() => handleDownloadOne(p.row as ContainerRentalDto)} showInMenu />,
          <GridActionsCellItem key="del" icon={<GridDeleteIcon />} label="삭제"
            onClick={() => handleDelete(p.row as ContainerRentalDto)} showInMenu />,
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [basePath]
  );

  const isEmpty = !loading && items.length === 0;
  const today = new Date();
  const stamp = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, "0")}.${String(today.getDate()).padStart(2, "0")}`;

  return (
    <Box
      sx={{
        // 대시보드 안에서는 부모 패딩을 음수 마진으로 상쇄해 edge-to-edge 로 두고,
        // 단독 경로에서는 자체 배경을 깐다.
        position: "relative",
        m: inDashboard ? { xs: -2, md: -3 } : 0,
        p: { xs: 2, md: 3 },
        minHeight: inDashboard ? "calc(100vh - 65px)" : "100vh",
        bgcolor: C.canvas,
        fontFamily: T.fonts.sans,
      }}
    >
      {/* 페이지 헤더 */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", md: "flex-end" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: T.fonts.sans,
              fontWeight: 700,
              fontSize: 22,
              lineHeight: 1.3,
              letterSpacing: "-0.01em",
              color: C.ink,
            }}
          >
            세금계산서 발행 목록
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              fontFamily: T.fonts.sans,
              fontSize: 13.5,
              color: C.sub,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            등록된 발행 건과 회차별 일정을 관리합니다 · 총 {total}건 · {stamp} 기준
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexShrink: 0, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={handleDownloadAll}
            startIcon={<FileDownloadIcon sx={{ fontSize: 18 }} />}
          >
            전체 엑셀
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate(`${basePath}/create`)}
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          >
            신규 등록
          </Button>
        </Box>
      </Box>

      {/* KPI */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
          gap: { xs: 1.25, md: 2 },
          mb: 2.5,
        }}
      >
        <KpiCard label="진행중"   value={stats.active}    accent={T.steel} />
        <KpiCard label="일시정지" value={stats.paused}    accent={T.amber} />
        <KpiCard label="종료"     value={stats.closed}    accent={C.caption} />
        <KpiCard label="취소"     value={stats.cancelled} accent={T.stop} />
      </Box>

      <RentalSearchBar value={params} onChange={handleParamsChange} />

      {/* 결과 수 + 뷰 전환 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            fontFamily: T.fonts.sans,
            fontSize: 13,
            color: C.sub,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          전체 {total}건 중 <Box component="span" sx={{ color: C.ink, fontWeight: 600 }}>{items.length}건</Box> 표시
        </Box>

        <Box
          sx={{
            display: "inline-flex",
            gap: 0.5,
            p: "3px",
            bgcolor: C.neutral,
            borderRadius: "8px",
          }}
        >
          <ViewToggleBtn
            active={view === "cards"}
            onClick={() => setView("cards")}
            icon={<GridViewIcon sx={{ fontSize: 15 }} />}
            label="카드"
          />
          <ViewToggleBtn
            active={view === "grid"}
            onClick={() => setView("grid")}
            icon={<TableRowsIcon sx={{ fontSize: 15 }} />}
            label="표"
          />
        </Box>
      </Box>

      {/* 로딩 */}
      {loading && items.length === 0 && (
        <Box
          sx={{
            display: "flex", justifyContent: "center", alignItems: "center",
            py: 10, flexDirection: "column", gap: 2,
          }}
        >
          <CircularProgress size={26} sx={{ color: C.accent }} />
          <Box sx={{ fontFamily: T.fonts.sans, fontSize: 13, color: C.sub }}>
            목록을 불러오는 중입니다…
          </Box>
        </Box>
      )}

      {/* 빈 상태 */}
      {isEmpty && (
        <Box
          sx={{
            bgcolor: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: "12px",
            boxShadow: "0 1px 2px rgba(16,24,32,0.06)",
            py: { xs: 6, md: 8 },
            px: 2,
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              mx: "auto",
              mb: 2,
              borderRadius: "12px",
              bgcolor: C.neutral,
              color: C.sub,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LocalShippingIcon sx={{ fontSize: 26 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: T.fonts.sans,
              fontWeight: 700,
              fontSize: 17,
              color: C.ink,
              mb: 0.75,
            }}
          >
            등록된 거래가 없습니다
          </Typography>
          <Typography
            sx={{
              fontFamily: T.fonts.sans,
              fontSize: 13.5,
              color: C.sub,
              mb: 3,
            }}
          >
            견적서를 불러와 등록하면 회차별 발행 일정이 자동으로 생성됩니다.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => navigate(`${basePath}/create`)}
          >
            첫 거래 등록하기
          </Button>
        </Box>
      )}

      {/* 카드 그리드 */}
      {!isEmpty && view === "cards" && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, 1fr)",
                xl: "repeat(3, 1fr)",
              },
              gap: { xs: 2, md: 2.5 },
              "& > *": {
                animation: "rentalCardIn 260ms cubic-bezier(0.2, 0.8, 0.2, 1) both",
              },
              "@keyframes rentalCardIn": {
                "0%": { opacity: 0, transform: "translateY(6px)" },
                "100%": { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            {items.map((r) => (
              <RentalCard
                key={r.id}
                rental={r}
                onOpen={handleOpen}
                onDelete={handleDelete}
                onDownloadExcel={handleDownloadOne}
              />
            ))}
          </Box>

          {items.length < total && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Button variant="outlined" onClick={handleLoadMore} disabled={loading}>
                더 보기
              </Button>
            </Box>
          )}
        </>
      )}

      {/* 표 보기 */}
      {!isEmpty && view === "grid" && (
        <Box
          sx={{
            bgcolor: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: "12px",
            boxShadow: "0 1px 2px rgba(16,24,32,0.06)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              bgcolor: C.field,
              borderBottom: `1px solid ${C.border}`,
              px: 2,
              py: 1.15,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box
              sx={{
                fontFamily: T.fonts.sans,
                fontSize: 13,
                fontWeight: 600,
                color: C.ink,
              }}
            >
              발행 목록
            </Box>
            <Box
              sx={{
                fontFamily: T.fonts.sans,
                fontSize: 12.5,
                color: C.sub,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {items.length}건
            </Box>
          </Box>
          <Box sx={{ height: 600 }}>
            <DataGridPro
              rows={items}
              columns={columns}
              getRowId={(r) => (r as ContainerRentalDto).id}
              loading={loading}
              pageSizeOptions={[20, 50, 100]}
              pagination
              onRowClick={(p) => handleOpen(String(p.id))}
              initialState={{
                pagination: { paginationModel: { pageSize: 20, page: 0 } },
              }}
              disableRowSelectionOnClick
              sx={{
                border: "none",
                borderRadius: 0,
                fontFamily: T.fonts.sans,
                "& .MuiDataGrid-row": { cursor: "pointer" },
              }}
              getRowClassName={(p) => {
                const r = p.row as ContainerRentalDto;
                return r.status === "closed" || r.status === "cancelled"
                  ? "rental-row--dim"
                  : "";
              }}
            />
          </Box>
          <style>{`.rental-row--dim { opacity: 0.6; }`}</style>
        </Box>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="거래를 삭제하시겠습니까?"
        message={
          deleteTarget
            ? `${deleteTarget.clientName} / ${deleteTarget.siteName} — 이 작업은 되돌릴 수 없습니다.`
            : ""
        }
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        loading={deleteSubmitting}
        confirmText="삭제"
        cancelText="취소"
      />

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            onClose={() => setSnack(null)}
            variant="filled"
            sx={{ width: "100%", borderRadius: "8px", fontFamily: T.fonts.sans }}
          >
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}

// ---------- Subcomponents ----------

function KpiCard({
  label, value, accent,
}: {
  label: string;
  value: number;
  accent: string;
}) {
  return (
    <Box
      sx={{
        bgcolor: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(16,24,32,0.06)",
        px: { xs: 1.75, md: 2.25 },
        py: { xs: 1.5, md: 1.75 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.85,
          fontFamily: T.fonts.sans,
          fontSize: 12.5,
          fontWeight: 500,
          color: C.sub,
          mb: 0.75,
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: accent,
            flexShrink: 0,
          }}
        />
        {label}
      </Box>
      <Box
        sx={{
          fontFamily: T.fonts.mono,
          fontWeight: 700,
          fontSize: { xs: 22, md: 25 },
          color: C.ink,
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {Number.isFinite(value) ? value : 0}
        <Box
          component="span"
          sx={{
            fontFamily: T.fonts.sans,
            fontSize: 12.5,
            fontWeight: 500,
            ml: 0.5,
            color: C.caption,
          }}
        >
          건
        </Box>
      </Box>
    </Box>
  );
}

function ViewToggleBtn({
  active, onClick, icon, label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        cursor: "pointer",
        userSelect: "none",
        outline: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        px: 1.5,
        py: 0.6,
        borderRadius: "6px",
        bgcolor: active ? C.surface : "transparent",
        color: active ? C.accentHover : C.sub,
        boxShadow: active ? "0 1px 2px rgba(16,24,32,0.06)" : "none",
        fontFamily: T.fonts.sans,
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        transition: "background-color 140ms ease, color 140ms ease",
        "&:hover": { color: active ? C.accentHover : C.ink },
        "&:focus-visible": {
          color: C.accentHover,
          boxShadow: `0 0 0 2px ${C.accentTint}`,
        },
      }}
    >
      {icon}
      {label}
    </Box>
  );
}
